import { useState, useRef } from 'react';
import { dbSurgeries, dbNotes, type Surgery, type PatientNote } from '../../../lib/localDB';

interface ImportModalProps {
  onClose: () => void;
  onImported: () => void;
}

type ImportData = {
  exportDate?: string;
  version?: string;
  counts?: { surgeries: number; teeth: number; notes: number };
  surgeries?: Surgery[];
  notes?: PatientNote[];
};

type ImportStep = 'select' | 'preview' | 'importing' | 'done';

export default function ImportModal({ onClose, onImported }: ImportModalProps) {
  const [step, setStep] = useState<ImportStep>('select');
  const [data, setData] = useState<ImportData | null>(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState<'merge' | 'replace'>('merge');
  const [progress, setProgress] = useState({ surgeries: 0, notes: 0 });
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');

    if (!file.name.endsWith('.json')) {
      setError('Sadece .json dosyaları desteklenmektedir.');
      return;
    }

    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as ImportData;

      if (!parsed.surgeries || !Array.isArray(parsed.surgeries)) {
        setError('Geçersiz yedek dosyası: "surgeries" dizisi bulunamadı.');
        return;
      }

      // Basic validation
      const invalidSurgery = parsed.surgeries.find((s) => !s.id || !s.patient_name);
      if (invalidSurgery) {
        setError('Geçersiz veri: Bazı ameliyat kayıtlarında id veya hasta adı eksik.');
        return;
      }

      setData(parsed);
      setStep('preview');
    } catch {
      setError('Dosya okunamadı veya geçersiz JSON formatı.');
    }
  };

  const handleImport = async () => {
    if (!data) return;
    setStep('importing');
    setProgress({ surgeries: 0, notes: 0 });

    try {
      const surgeries = data.surgeries || [];
      const notes = data.notes || [];

      if (mode === 'replace') {
        // Clear existing data first
        const existingSurgeries = await dbSurgeries.getAll();
        for (const s of existingSurgeries) {
          await dbSurgeries.delete(s.id);
        }
        const existingNotes = await dbNotes.getAll();
        for (const n of existingNotes) {
          await dbNotes.delete(n.id);
        }
      }

      // Import surgeries
      let surgeryCount = 0;
      for (const surgery of surgeries) {
        try {
          await dbSurgeries.update(surgery.id, surgery);
          surgeryCount++;
          setProgress((p) => ({ ...p, surgeries: surgeryCount }));
        } catch (err) {
          console.error(`Ameliyat import hatası (${surgery.id}):`, err);
        }
      }

      // Import notes
      let noteCount = 0;
      for (const note of notes) {
        try {
          await dbNotes.update(note.id, note);
          noteCount++;
          setProgress((p) => ({ ...p, notes: noteCount }));
        } catch (err) {
          console.error(`Not import hatası (${note.id}):`, err);
        }
      }

      setStep('done');
      setTimeout(() => {
        onImported();
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Import hatası:', err);
      setError('İçe aktarma sırasında bir hata oluştu.');
      setStep('preview');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Yedek Yükle</h3>
            <p className="text-sm text-gray-500 mt-0.5">JSON yedek dosyasından geri yükleyin</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer transition-all">
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Step 1: Select File */}
          {step === 'select' && (
            <>
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-gray-200 hover:border-teal-400 rounded-xl p-8 text-center cursor-pointer transition-all hover:bg-teal-50/30"
              >
                <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-upload-cloud-line text-2xl text-teal-600"></i>
                </div>
                <p className="text-sm font-semibold text-gray-700 mb-1">JSON dosyası seçin</p>
                <p className="text-xs text-gray-400">"Yedek Al" ile dışa aktarılan dosyayı yükleyin</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept=".json"
                onChange={handleFile}
                className="hidden"
              />
              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
                  <i className="ri-error-warning-line text-lg flex-shrink-0 mt-0.5"></i>
                  <span>{error}</span>
                </div>
              )}
            </>
          )}

          {/* Step 2: Preview */}
          {step === 'preview' && data && (
            <>
              <div className="bg-teal-50 border border-teal-200 rounded-xl p-4">
                <p className="text-sm font-semibold text-teal-800 mb-3 flex items-center gap-2">
                  <i className="ri-file-text-line text-lg"></i>
                  Yedek Bilgisi
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-teal-600">{data.surgeries?.length ?? 0}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Ameliyat</p>
                  </div>
                  <div className="bg-white rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-teal-600">{data.notes?.length ?? 0}</p>
                    <p className="text-xs text-gray-500 mt-0.5">Not</p>
                  </div>
                </div>
                {data.exportDate && (
                  <p className="text-xs text-teal-600 mt-3">
                    Yedekleme tarihi: {new Date(data.exportDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
              </div>

              {/* Mode selection */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-3">Yükleme Modu</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setMode('merge')}
                    className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${mode === 'merge' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className="w-9 h-9 bg-teal-100 rounded-lg flex items-center justify-center mb-2">
                      <i className="ri-git-merge-line text-lg text-teal-600"></i>
                    </div>
                    <p className={`font-semibold text-sm ${mode === 'merge' ? 'text-teal-700' : 'text-gray-900'}`}>Birleştir</p>
                    <p className="text-xs text-gray-500 mt-0.5">Mevcut verilerin üzerine ekle</p>
                  </button>
                  <button
                    onClick={() => setMode('replace')}
                    className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${mode === 'replace' ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                  >
                    <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center mb-2">
                      <i className="ri-refresh-line text-lg text-red-600"></i>
                    </div>
                    <p className={`font-semibold text-sm ${mode === 'replace' ? 'text-red-700' : 'text-gray-900'}`}>Sıfırla & Yükle</p>
                    <p className="text-xs text-gray-500 mt-0.5">Tüm veriyi silip yedeği yükle</p>
                  </button>
                </div>
              </div>

              {mode === 'replace' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                  <i className="ri-alert-line text-red-500 text-lg flex-shrink-0 mt-0.5"></i>
                  <p className="text-xs text-red-700">
                    <strong>Dikkat:</strong> Bu mod mevcut tüm ameliyat ve not verilerini silecek ve sadece yedekteki verileri yükleyecektir. Bu işlem geri alınamaz.
                  </p>
                </div>
              )}

              {error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
                  <i className="ri-error-warning-line text-lg flex-shrink-0 mt-0.5"></i>
                  <span>{error}</span>
                </div>
              )}

              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => { setStep('select'); setData(null); setError(''); }}
                  className="flex-1 py-3 px-4 border border-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  Geri
                </button>
                <button
                  onClick={handleImport}
                  className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer ${
                    mode === 'replace'
                      ? 'bg-red-500 text-white hover:bg-red-600'
                      : 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white hover:from-teal-600 hover:to-cyan-700'
                  }`}
                >
                  <i className="ri-upload-2-line text-lg"></i>
                  {mode === 'replace' ? 'Sıfırla & Yükle' : 'Birleştir'}
                </button>
              </div>
            </>
          )}

          {/* Step 3: Importing */}
          {step === 'importing' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-sm font-semibold text-gray-700 mb-3">İçe aktarılıyor...</p>
              <div className="space-y-2 text-sm text-gray-500">
                <p>Ameliyatlar: {progress.surgeries} / {data?.surgeries?.length ?? 0}</p>
                <p>Notlar: {progress.notes} / {data?.notes?.length ?? 0}</p>
              </div>
            </div>
          )}

          {/* Step 4: Done */}
          {step === 'done' && (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-double-line text-3xl text-green-600"></i>
              </div>
              <p className="text-lg font-bold text-gray-900 mb-1">Başarıyla Yüklendi!</p>
              <p className="text-sm text-gray-500">
                {progress.surgeries} ameliyat, {progress.notes} not içe aktarıldı.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
